import React, { useEffect } from 'react'
import {
  useParams,
  useNavigate,
  useLocation,
  Link,
} from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Button } from '../../ui/button'
import { Badge } from '../../ui/badge'
import { Progress } from '../../ui/progress'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../ui/breadcrumb'
import {
  Loader2,
  Wand2,
  Check,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { EventProvider, useEventContext } from '../EventContext'
import { useSessionStore } from '../../../stores/useSessionStore'
import { useEventSetupStatus } from './useEventSetupStatus'
import { WIZARD_STEPS, wizardHref } from './eventSetupStatus'
import { SETUP_STEP_ICONS } from './setupStepIcons'
import { EventSetupChecklist } from './EventSetupChecklist'
import { BasicsStep } from './steps/BasicsStep'
import { ItemsStep } from './steps/ItemsStep'
import { RolesStep } from './steps/RolesStep'
import { StaffStep } from './steps/StaffStep'
import { FinishStep } from './steps/FinishStep'

const FINISH = 'finish'

export const ScheduleSetupWizard: React.FC = () => (
  <EventProvider>
    <ScheduleSetupWizardInner />
  </EventProvider>
)

const ScheduleSetupWizardInner: React.FC = () => {
  const { eventId, step } = useParams<{ eventId: string; step?: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { event, loading, canEdit } = useEventContext()
  const { hasAdmin, hasIdentityManagerFor } = useSessionStore()

  const { status, loadingStaff, refreshStaff } = useEventSetupStatus(event, {
    includeStaff: true,
  })

  const canProvision = !!(
    hasAdmin() || (event?.client && hasIdentityManagerFor(event.client.id))
  )

  // Where to send the user when they finish or exit, e.g. back to the schedule
  // page they were bounced from.
  const returnTo = new URLSearchParams(location.search).get('from') || undefined

  const stepIndex = WIZARD_STEPS.findIndex((s) => s.wizardPath === step)
  const isFinish = step === FINISH
  const currentStep = stepIndex >= 0 ? WIZARD_STEPS[stepIndex] : null

  const go = (path: string, replace = false) =>
    navigate(`${wizardHref(eventId!, path)}${location.search}`, { replace })

  // Land on the first thing that still needs doing when no step is named, and
  // recover from a bad path rather than rendering nothing.
  useEffect(() => {
    if (loading || !event) return
    if (currentStep || isFinish) return
    const target =
      status.firstIncompleteStepId &&
      WIZARD_STEPS.some((s) => s.id === status.firstIncompleteStepId)
        ? status.firstIncompleteStepId
        : WIZARD_STEPS[0].id
    go(target, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, event, step])

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Loading event...
        </p>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p className="text-center text-muted-foreground">Event not found</p>
      </div>
    )
  }

  const header = (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/event">Events</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href={`/event/${eventId}/config`}>
              {event.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-semibold">Setup</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    </>
  )

  // Steps here all write to the event, so a viewer without schedule-admin can't
  // complete any of them. Show them what's outstanding instead of a dead end.
  if (!canEdit) {
    return (
      <div className="container mx-auto space-y-6 px-4 py-6">
        {header}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Schedule setup</CardTitle>
            <CardDescription>
              Setting up {event.name} needs schedule administrator access. Here is
              where the event stands — ask an administrator to finish the
              outstanding steps.
            </CardDescription>
          </CardHeader>
        </Card>
        <EventSetupChecklist
          eventId={eventId!}
          status={status}
          canEdit={false}
          canProvision={canProvision}
          showWizardButton={false}
        />
      </div>
    )
  }

  const total = WIZARD_STEPS.length

  return (
    <div className="container mx-auto space-y-6 px-4 py-6">
      {header}

      {/* Header + progress rail */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Wand2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">Schedule Setup Wizard</CardTitle>
                <CardDescription className="mt-1 flex items-center gap-3">
                  <span>{event.name}</span>
                  {event.client && (
                    <Badge variant="secondary">{event.client.name}</Badge>
                  )}
                </CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate(returnTo || `/event/${eventId}/config`)}
            >
              <X className="mr-2 h-4 w-4" />
              Exit setup
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Progress value={status.percent} className="flex-1" />
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {status.completedCount} of {status.requiredCount} complete
            </span>
          </div>

          <ol className="flex flex-wrap gap-2">
            {WIZARD_STEPS.map((meta, index) => {
              const state = status.byId[meta.id]
              const active = !isFinish && meta.id === currentStep?.id
              const Icon = SETUP_STEP_ICONS[meta.id]
              return (
                <li key={meta.id}>
                  <button
                    type="button"
                    onClick={() => go(meta.wizardPath!)}
                    className={cn(
                      'flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
                      active
                        ? 'border-primary bg-primary/10 font-medium text-primary'
                        : 'hover:bg-muted/50'
                    )}
                    aria-current={active ? 'step' : undefined}
                  >
                    {state.done ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="text-muted-foreground">{index + 1}.</span>
                    <span className={active ? '' : 'text-foreground'}>
                      {meta.label}
                    </span>
                  </button>
                </li>
              )
            })}
          </ol>
        </CardContent>
      </Card>

      {/* Step body */}
      {isFinish ? (
        <FinishStep eventId={eventId!} status={status} returnTo={returnTo} />
      ) : currentStep ? (
        <>
          <p className="text-sm text-muted-foreground">
            Step {stepIndex + 1} of {total} — {currentStep.label}
            {currentStep.optional && ' (recommended)'}
          </p>

          {currentStep.id === 'basics' && (
            <BasicsStep
              event={event}
              step={status.byId.basics}
              canEdit={canEdit}
            />
          )}
          {currentStep.id === 'locations' && (
            <ItemsStep kind="location" step={status.byId.locations} />
          )}
          {currentStep.id === 'positions' && (
            <ItemsStep kind="position" step={status.byId.positions} />
          )}
          {currentStep.id === 'roles' && <RolesStep step={status.byId.roles} />}
          {currentStep.id === 'staff' && (
            <StaffStep
              event={event}
              step={status.byId.staff}
              rolesDone={status.byId.roles.done}
              canProvision={canProvision}
              loading={loadingStaff}
              onRefresh={refreshStaff}
            />
          )}

          <WizardFooter
            done={status.byId[currentStep.id].done}
            consequence={currentStep.consequence}
            onBack={
              stepIndex > 0
                ? () => go(WIZARD_STEPS[stepIndex - 1].wizardPath!)
                : undefined
            }
            onNext={() =>
              stepIndex < total - 1
                ? go(WIZARD_STEPS[stepIndex + 1].wizardPath!)
                : go(FINISH)
            }
            nextLabel={stepIndex < total - 1 ? 'Next' : 'Finish'}
          />
        </>
      ) : null}
    </div>
  )
}

interface WizardFooterProps {
  done: boolean
  consequence: string
  onBack?: () => void
  onNext: () => void
  nextLabel: string
}

/**
 * Never blocks the user — an unfinished step can be skipped, but we say plainly
 * what skipping it costs so they aren't surprised on the schedule page later.
 */
const WizardFooter: React.FC<WizardFooterProps> = ({
  done,
  consequence,
  onBack,
  onNext,
  nextLabel,
}) => (
  <Card>
    <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
      <div className="min-w-0 text-sm text-muted-foreground">
        {!done && consequence && <span>Skipping this step means {consequence}.</span>}
      </div>
      <div className="flex shrink-0 gap-2">
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        )}
        <Button variant={done ? 'default' : 'outline'} onClick={onNext}>
          {done ? nextLabel : 'Skip for now'}
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
)
